// File: src/pages/WhatHeliaIsLike.js
import React, { useContext } from "react";
import { Helmet } from "react-helmet";
import { Routes, Route, Link } from "react-router-dom";
import { MobileViewContext } from "../context/MobileViewContext";
import SecondarySidebarBasic from "../components/SecondarySidebarBasic";
import "./WhatHeliaIsLike.css";

export const whatHeliaIsLikeItems = [
  { label: "Why this page exists", route: "/what-helia-is-like" },
  { label: "Films", route: "/what-helia-is-like/films" },
  { label: "Music", route: "/what-helia-is-like/music" },
];

function Intro() {
  return (
    <div className="whil-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>What Helia Is Like | Helia Jamshidi</title>
        <meta
          name="description"
          content="A sneak peek at Helia Jamshidi: the answers to questions a friend might ask, so we can skip them and talk about something new."
        />
        <meta property="og:title" content="What Helia Is Like | Helia Jamshidi" />
        <meta
          property="og:description"
          content="Not suggestions. A small spy tool for seeing something about Helia without her having to tell it to you."
        />
        <meta property="og:url" content="https://heliajamshidi.me/what-helia-is-like" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://heliajamshidi.me/what-helia-is-like" />
      </Helmet>

      <h2>What Helia is like</h2>

      <p className="whil-lead">
        This section is not here to give you suggestions.
      </p>
      <p>
        It is an attempt to offer some answers to the questions a friend might ask me in person.
        The honest reason is that I am bored of answering any question I have already answered once, to anybody.
        So this is a sneak peek, a small spy tool: you get to see something about me without me having to tell it to you.
      </p>
      <p>
        I hope it leaves room for us to talk about the topics where we both find out things, about me or about you, in real time.
        Those are the conversations I actually want.
      </p>
      <p>
        The pages here are written in third person, on purpose. They are not complete lists, just a tangible direction.
        If you recognise yourself in some of it, or you strongly do not, either one is a good place to start.
      </p>

      <ul className="whil-index">
        <li>
          <Link to="/what-helia-is-like/films">Films</Link>
          <span>What she watches, what she skips, and why.</span>
        </li>
        <li>
          <Link to="/what-helia-is-like/music">Music</Link>
          <span>What is actually playing, once you take the DJ work out.</span>
        </li>
      </ul>
    </div>
  );
}

function Films() {
  return (
    <div className="whil-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Films She Likes | What Helia Is Like</title>
        <meta
          name="description"
          content="A tangible direction, not a complete list: the films and directors Helia Jamshidi keeps coming back to, and the kind she walks out of."
        />
        <meta property="og:title" content="Films She Likes | What Helia Is Like" />
        <meta property="og:url" content="https://heliajamshidi.me/what-helia-is-like/films" />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://heliajamshidi.me/what-helia-is-like/films" />
      </Helmet>

      <h2>Films</h2>
      <p className="whil-note">
        Third person, by design. Not comprehensive. Enough to know where to point.
      </p>

      <h3>The short version</h3>
      <p>
        Helia wants sincerity under pressure. Her favourite films are almost all about an ordinary person squeezed by circumstance,
        told by a filmmaker who refuses to wink at the audience. The camera can be as slow and poetic as Tarkovsky or as plain as Farhadi;
        what matters is that the feeling is direct. Irony reads to her as a film protecting itself, and she loses interest fast.
      </p>
      <p>
        She watched a lot until about 2017 and much less since. So if a film is recent, assume she has not seen it, and assume she would like to catch up.
      </p>

      <h3>Where she keeps returning</h3>
      <ul className="whil-list">
        <li>
          <strong>Iranian cinema, root to branch.</strong> Kiarostami above all (<em>Close-Up</em>, <em>Where Is the Friend's House?</em>),
          Mehrjui's <em>The Cow</em>, Beyzai's <em>Bashu</em>, and early Farhadi: <em>Fireworks Wednesday</em> and <em>About Elly</em> sit at the very top.
        </li>
        <li>
          <strong>Tarkovsky and his heirs.</strong> <em>Nostalghia</em>, <em>Mirror</em>, <em>Stalker</em>; then Zvyagintsev's <em>The Return</em>,
          Ceylan's <em>Winter Sleep</em>, Parajanov's <em>The Color of Pomegranates</em>.
        </li>
        <li>
          <strong>Jim Jarmusch's first decade.</strong> <em>Stranger Than Paradise</em>, <em>Mystery Train</em>, <em>Night on Earth</em>, <em>Down by Law</em>.
          She did not like <em>Paterson</em>.
        </li>
        <li>
          <strong>Classic Hollywood musicals and Billy Wilder.</strong> <em>Singin' in the Rain</em>, <em>Sweet Charity</em>, <em>Cabaret</em>, <em>All That Jazz</em>,
          and nearly everything Wilder made: <em>The Apartment</em>, <em>Some Like It Hot</em>, <em>Sunset Boulevard</em>, <em>Sabrina</em>.
        </li>
        <li>
          <strong>The French New Wave, Truffaut first.</strong> <em>The 400 Blows</em>, <em>Jules and Jim</em>, then Godard's <em>Breathless</em> and <em>Pierrot le Fou</em>.
        </li>
        <li>
          <strong>New Hollywood, 1967 to 1982.</strong> <em>One Flew Over the Cuckoo's Nest</em>, <em>Taxi Driver</em>, <em>Tootsie</em>, <em>Kramer vs. Kramer</em>,
          Redford, Newman, young Dustin Hoffman. Woody Allen from <em>Annie Hall</em> to <em>Hannah and Her Sisters</em>, and not much after.
        </li>
        <li>
          <strong>Kieslowski, Haneke, von Trier.</strong> <em>Three Colours: Red</em>, <em>Cache</em>, <em>Amour</em>, <em>Dancer in the Dark</em>, <em>Melancholia</em>.
          Cruelty is fine when it means something.
        </li>
        <li>
          <strong>Longing.</strong> <em>Lost in Translation</em>, <em>In the Mood for Love</em>, <em>Call Me by Your Name</em>, <em>Cold War</em>, <em>Brief Encounter</em>.
        </li>
        <li>
          <strong>Scattered loves that fit the pattern.</strong> <em>Parasite</em>, <em>Inglourious Basterds</em>, <em>Eternal Sunshine of the Spotless Mind</em>,
          <em>Toni Erdmann</em>, <em>The Lives of Others</em>, <em>Shoplifters</em>, <em>Perfect Days</em>, <em>The Zone of Interest</em>.
        </li>
      </ul>

      <h3>What she walks out of</h3>
      <p>
        Three patterns cover most of her dislikes. Late-career work by directors she loves: she likes directors hungry, not comfortable, so
        <em> A Hero</em>, <em>Once Upon a Time in Hollywood</em>, <em>The Lobster</em> and late Woody Allen all missed.
        Films whose main register is irony or self-congratulation: <em>The Big Lebowski</em>, <em>Three Billboards</em>, <em>Lady Bird</em>,
        <em>When Harry Met Sally</em>, <em>Dead Poets Society</em>. And modern American comedy in general. Older comedy, and comedy from anywhere else, is welcome.
      </p>

      <h3>Gaps she knows about</h3>
      <p>
        Bergman and Fellini's <em>8 1/2</em> did not land, and she suspects that is missing context rather than taste, so she is working toward them.
        Kurosawa, Ozu, Bresson, Hitchcock, most Korean cinema, and most Iranian art house after 1990 are still largely unexplored.
        If you have a way in to any of these, that is a conversation she would rather have than the one about her favourites.
      </p>
    </div>
  );
}

function Music() {
  return (
    <div className="whil-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Music She Likes | What Helia Is Like</title>
        <meta
          name="description"
          content="A tangible direction, not a complete list: what Helia Jamshidi actually listens to, once the tango DJ material is set aside."
        />
        <meta property="og:title" content="Music She Likes | What Helia Is Like" />
        <meta property="og:url" content="https://heliajamshidi.me/what-helia-is-like/music" />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://heliajamshidi.me/what-helia-is-like/music" />
      </Helmet>

      <h2>Music</h2>
      <p className="whil-note">
        Third person, by design. Not comprehensive. Enough to know where to point.
      </p>

      <h3>One caveat first</h3>
      <p>
        Helia DJs tango. Roughly half of her library is Golden Age orchestras (Troilo, Pugliese, Di Sarli, D'Arienzo, Caló) and a dozen versions of the
        same song saved side by side for comparison. That is working material. She loves it, but it is craft as much as taste, so it is set aside here.
        The rest is what she actually puts on.
      </p>

      <h3>What is usually playing</h3>
      <ul className="whil-list">
        <li>
          <strong>Persian, the biggest block.</strong> Shajarian, father and son, Hossein Alizadeh, Kayhan Kalhor. Also Ahmad Shamlu reading
          Hafez, Rumi, Khayyam and Lorca aloud, which she keeps as albums. Southern and folk voices like Soheil Nafisi and Ebrahim Monsefi.
        </li>
        <li>
          <strong>Minimalism and neoclassical.</strong> Philip Glass in bulk (<em>Akhnaten</em>, <em>Passages</em> with Ravi Shankar), Max Richter,
          Arvo Pärt, Yann Tiersen, Preisner, Eleni Karaindrou. Max Richter shows up in every listening window Spotify reports for her.
        </li>
        <li>
          <strong>Anouar Brahem.</strong> Five albums, and the other steady presence alongside Richter. Le Trio Joubran nearby.
        </li>
        <li>
          <strong>Weimar and cabaret.</strong> Moka Efti Orchestra, the <em>Babylon Berlin</em> soundtrack, Hollaender and Spoliansky, Claire Waldoff,
          Marlene Dietrich, Max Raabe, and a stack of Tiger Lillies records.
        </li>
        <li>
          <strong>Tom Waits.</strong> <em>Rain Dogs</em>, <em>Alice</em>, <em>Mule Variations</em>, and more.
        </li>
        <li>
          <strong>Musicals and old Hollywood.</strong> <em>My Fair Lady</em>, <em>Cabaret</em>, Gwen Verdon, Donald O'Connor, Liza Minnelli.
          This is the same corner of her as the film page.
        </li>
        <li>
          <strong>Vintage jazz and swing.</strong> Fats Waller, Billie Holiday, Louis Armstrong, Benny Goodman, the Boswell Sisters.
        </li>
        <li>
          <strong>Fado and the Mediterranean.</strong> Amália Rodrigues, Mariza, Carlos do Carmo, Yasmin Levy, Souad Massi, Sezen Aksu.
        </li>
        <li>
          <strong>Klezmer and Balkan brass.</strong> Barcelona Gipsy Klezmer Orchestra, Taraf de Haïdouks, the Klezmatics.
        </li>
        <li>
          <strong>A rock streak she does not hide.</strong> The Beatles, Pink Floyd, Camel, Queen, Metallica, System of a Down, Bob Dylan, Leonard Cohen.
        </li>
      </ul>

      <h3>How to read this</h3>
      <p>
        The thread is voice and melancholy over production. Almost nothing here is loud in a modern way, and almost nothing is background music.
        If you put on Amália, Shajarian, or a slow Richter piece, you have her attention. Early rock and roll, Little Richard and Chuck Berry, has been creeping into her recent plays too.
      </p>
    </div>
  );
}

export default function WhatHeliaIsLike({ isMobile }) {
  const { contentIsVisibleMobile, toc2IsVisibleMobile } = useContext(MobileViewContext);
  return (
    <div className="content-with-sub">
      {(!isMobile || toc2IsVisibleMobile) && (
        <SecondarySidebarBasic heading="What Helia Is Like" items={whatHeliaIsLikeItems} isMobile={isMobile} />
      )}
      {(!isMobile || contentIsVisibleMobile) && (
        <div className="content-area">
          <Routes>
            <Route path="/" element={<Intro />} />
            <Route path="/films" element={<Films />} />
            <Route path="/music" element={<Music />} />
          </Routes>
        </div>
      )}
    </div>
  );
}
